import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api.js';

interface HouseholdWeek {
  sharing: boolean;
  members: { name: string; me: boolean; done: number; planned: number }[];
}

/**
 * Gemeinsames Wochenziel (E13): zusammen statt gegeneinander.
 *
 * Kommt vom Server (die Trainings der anderen liegen nicht auf diesem Gerät)
 * — nur Zahlen, nur von denen, die teilen. Offline oder ohne Teilen: nichts.
 */
export function HouseholdGoal(): React.JSX.Element | null {
  const { t } = useTranslation();
  const [data, setData] = useState<HouseholdWeek | null>(null);
  useEffect(() => {
    let alive = true;
    // Einmal beim Öffnen; offline oder ohne Haushalt bleibt die Kachel weg.
    apiFetch<HouseholdWeek>('/households/week').then(
      (week) => alive && setData(week),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, []);
  if (!data || !data.sharing) return null;

  const done = data.members.reduce((sum, member) => sum + member.done, 0);
  const planned = data.members.reduce((sum, member) => sum + member.planned, 0);
  const closed = planned > 0 && done >= planned;

  return (
    <section aria-labelledby="household-goal" className="kw-tile flex flex-col gap-2">
      <h2 id="household-goal" className="kw-kicker">
        {t('householdGoal.heading')}
      </h2>
      {data.members.length < 2 ? (
        <p className="text-sm text-ink-muted">{t('householdGoal.alone')}</p>
      ) : (
        <>
          <p className={`text-base font-semibold ${closed ? 'kw-pop text-good' : ''}`}>
            {planned > 0
              ? t('householdGoal.together', { done, planned })
              : t('householdGoal.noPlan', { done })}
          </p>
          <ul className="flex flex-col gap-0.5 text-sm text-ink-muted">
            {data.members.map((member) => (
              <li key={member.name + String(member.me)}>
                {member.me ? t('householdGoal.me') : member.name}:{' '}
                {t('householdGoal.member', { done: member.done, planned: member.planned })}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
